import { useContext, useState } from 'react'
import { createPortal } from 'react-dom'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Modal from '../common/Modal'
import Tooltip from '../common/Tooltip'
import CardHeaderSlotContext from '../../StateManagment/CardHeaderSlot'
import InlineDetailsContext from '../../StateManagment/InlineDetailsContext'

/**
 * Filter control for dashboard / analytics cards. Renders a filter icon into
 * the card bottom bar's filter slot (inline at the call site when there is no
 * card chrome). Clicking it opens a modal with the filter chips; active
 * filters show as a count badge on the icon.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children - Filter chips shown in the modal.
 * @param {() => void} [props.onClear] - Clears the active filter.
 * @param {number} [props.activeFilterCount] - Number of active filters (shown as a badge on the icon).
 * @param {boolean} [props.hasFilters] - When false, nothing renders. Use this to avoid an icon that opens an empty modal.
 * @returns {JSX.Element|null}
 */
const DashboardFilterBar = ({ children, onClear, activeFilterCount = 0, hasFilters = true }) => {
    const slots = useContext(CardHeaderSlotContext)
    // Deep-analysis pages show a labelled button instead of a bare icon.
    const onDeepAnalysis = useContext(InlineDetailsContext)
    const [modalOpen, setModalOpen] = useState(false)

    // Nothing to filter → don't show an icon that opens an empty modal.
    if (!hasFilters) return null

    const trigger = onDeepAnalysis ? (
        <button
            type='button'
            className={`deep-analysis-ctrl deep-analysis-ctrl--filter${activeFilterCount > 0 ? ' deep-analysis-ctrl--active' : ''}`}
            onClick={() => setModalOpen(true)}
        >
            <FilterAltOutlinedIcon />
            <span>Filters</span>
            {activeFilterCount > 0 && (
                <span className='dashboard-filter-trigger-badge'>{activeFilterCount}</span>
            )}
            <ExpandMoreIcon className='deep-analysis-ctrl-chevron' />
        </button>
    ) : (
        <span className='dashboard-filter-trigger' onClick={() => setModalOpen(true)}>
            <Tooltip content='Filters' top='-35px'>
                <FilterAltOutlinedIcon className='dashboard-card-options-icon' />
            </Tooltip>
            {activeFilterCount > 0 && (
                <span className='dashboard-filter-trigger-badge'>{activeFilterCount}</span>
            )}
        </span>
    )

    // Chips apply their filter on change, so the modal only needs a close
    // button — plus a clear action when a filter is active.
    const content = (
        <div className='dashboard-filter-modal-body'>
            <div className='dashboard-filter-bar-chips'>
                {children}
            </div>
            {activeFilterCount > 0 && onClear && (
                <button
                    type='button'
                    className='dashboard-filter-bar-clear'
                    onClick={onClear}
                >
                    <CloseIcon fontSize='small' />
                    <span>Filters wissen</span>
                </button>
            )}
        </div>
    )

    return (
        <>
            {slots?.filterSlot ? createPortal(trigger, slots.filterSlot) : trigger}
            <Modal
                modalOpen={modalOpen}
                setModalOpen={setModalOpen}
                pageHeader='Filters'
                content={content}
                hideConfirm
                cancelLabel='Sluiten'
            />
        </>
    )
}

export default DashboardFilterBar
